<template>
  <div class="w-full">
    <div class="flex items-center mb-1" :class="labelWrapperClasses">
      <label class="o-caption" :for="id" v-text="`${label}${required ? '*' : ''}`" />
    </div>
    <div v-if="hasError && !disabled" class="error pb-1 mt-0">
      <OSvg svg="danger" class="fill-red-300" width="14" />
      <span class="o-caption" v-text="error" />
    </div>
    <div :class="wrapperClasses" class="input-wrapper">
      <div class="input-icon">
        <slot name="icon" />
      </div>
      <input
        v-if="tag === 'input'"
        :id="id"
        v-model="inputValue"
        class="input"
        :class="inputClasses"
        :type="type"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :aria-label="ariaLabel || label"
        :min="min"
        :pattern="pattern"
        :title="title"
        :max="max"
        :autocomplete="autoComplete"
        @change="$emit('change', $event)"
        @keydown="preventUnwantedSymbolInput"
      >
      <textarea
        v-else-if="tag === 'textarea'"
        :id="id"
        v-model="inputValue"
        class="input"
        :class="inputClasses"
        :type="type"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
      />
    </div>
  </div>
</template>

<script>
import { escapeRegExp } from '@/utils/regex'
const OSvg = () => import('@/components/OSvg/OSvg')

export default {
  name: 'OInput',
  components: {
    OSvg
  },
  props: {
    ariaLabel: {
      type: String,
      default: ''
    },
    tag: {
      type: String,
      default: 'input',
      validator: (value) => {
        return ['input', 'textarea'].includes(value)
      }
    },
    id: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    displayLabel: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      default: 'text'
    },
    value: {
      type: [String, Number],
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    error: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    autoComplete: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    inputClasses: {
      type: Array,
      default: () => []
    },
    min: {
      type: String,
      default: null
    },
    max: {
      type: String,
      default: null
    },
    pattern: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: null
    },
    excludeSymbols: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      inputValue: ''
    }
  },
  computed: {
    wrapperClasses () {
      const { $slots, hasError, disabled } = this

      return [
        { 'input-wrapper-has-icon': !!$slots.icon && $slots.icon.length },
        { 'input-wrapper-error': hasError },
        { 'input-wrapper-disabled': disabled }
      ]
    },
    hasError () {
      return this.error.length
    },
    labelWrapperClasses () {
      const classes = []

      if (!this.displayLabel) {
        classes.push('sr-only')
      }

      classes.push(this.required ? 'justify-start' : 'justify-between')

      return classes
    },
    excludeSymbolsRegex () {
      return this.excludeSymbols ? new RegExp(`[${escapeRegExp(this.excludeSymbols)}]`) : null
    }
  },
  watch: {
    value () {
      this.inputValue = this.value
    },
    inputValue () {
      if (this.excludeSymbolsRegex) {
        this.inputValue = this.inputValue?.replace(this.excludeSymbolsRegex, '')
      }
      this.$emit('input', this.inputValue)
    }
  },
  created () {
    this.inputValue = this.value
  },
  methods: {
    preventUnwantedSymbolInput (event) {
      if (!this.excludeSymbolsRegex) {
        return
      }
      if (this.excludeSymbolsRegex.test(event.key)) {
        event.preventDefault()
      }
    }
  }
}
</script>

<style lang="postcss" scoped>
.label {
  @apply block font-body text-sm leading-tight mb-2;
}

.error {
  @apply flex items-center text-red-300;

  svg {
    @apply flex mr-1
  }
}

textarea.input {
  @apply w-full;
}

.input {
  @apply relative border border-gray-600 rounded text-md leading-none;
  padding: 0.9rem 1.1rem;

  &::placeholder {
    @apply text-gray-400;
  }

  &-icon {
    @apply absolute hidden bg-primary rounded-l-sm;
    height: calc(100% - 2px);
    width: 5rem;
    left: 1px;
    bottom: 1px;
    z-index: 1;

    svg {
      @apply w-6;
    }

    path {
      @apply text-white fill-current stroke-current;
    }
  }

  &-wrapper {
    @apply relative;

    &-error {
      .input {
        @apply border-red-300;

        &-icon {
          @apply bg-red-300;
        }
      }
    }

    &-disabled {
      .input {
        @apply bg-gray-200 border border-gray-200 cursor-not-allowed;

        &-icon {
          @apply bg-gray-300;
        }
      }
    }

    &-has-icon {
      .input {
        @apply pl-12;

        &-icon {
          @apply flex justify-center items-center;
        }
      }
    }
  }
}
</style>
